import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ListFilter } from 'lucide-react';

/**
 * Excel-style multi-select column header filter.
 * `options` = array of unique { value, label } for this column.
 * `selected` = Set of currently-selected values (empty Set = no filter applied).
 * `onChange(newSet)` fires on Apply.
 */
export const ColumnFilterPopover = ({ label, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(selected);

  const filteredOptions = useMemo(
    () => options.filter(o => String(o.label ?? '').toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );

  const handleOpenChange = (next) => {
    if (next) setDraft(new Set(selected));
    setOpen(next);
  };

  const toggleValue = (value) => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  };

  const isActive = selected.size > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${isActive ? 'text-primary font-semibold' : ''}`}
          data-testid={testId || `col-filter-${label}`}
        >
          {label}
          <ListFilter className={`w-3 h-3 ${isActive ? 'fill-primary/20' : ''}`} />
          {isActive && (
            <span className="ml-0.5 text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 leading-4">
              {selected.size}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <Input
          placeholder={`Search ${label.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 mb-2 text-sm"
          data-testid={`col-filter-search-${label}`}
        />
        <div className="flex items-center justify-between mb-2 text-xs">
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setDraft(new Set(options.map(o => o.value)))}
          >
            Select all
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:underline"
            onClick={() => setDraft(new Set())}
          >
            Clear
          </button>
        </div>
        <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
          {filteredOptions.length > 0 ? filteredOptions.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
              <Checkbox
                checked={draft.has(opt.value)}
                onCheckedChange={() => toggleValue(opt.value)}
                data-testid={`col-filter-option-${label}-${opt.value}`}
              />
              <span className="truncate">{opt.label}</span>
            </label>
          )) : (
            <p className="text-xs text-muted-foreground py-2 text-center">No matches</p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-2 border-t">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            size="sm"
            className="bg-lime text-lime-foreground hover:bg-lime/90"
            onClick={() => { onChange(draft); setOpen(false); }}
            data-testid={`col-filter-apply-${label}`}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnFilterPopover;
